(() => {
  // Autoplay is held until window `load`: a slideshow advancing during page load churns
  // Speed Index and main-thread work (per-frame autoplay ticks + slide transitions), and
  // the first slide is on screen anyway.
  let pageLoaded = document.readyState === "complete";
  const onPageLoad = [];
  if (!pageLoaded) {
    window.addEventListener(
      "load",
      () => {
        pageLoaded = true;
        onPageLoad.forEach((cb) => cb());
      },
      { once: true }
    );
  }

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const prevSlide = swiper.slides[swiper.previousIndex];
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      const videoActive = activeSlide.querySelector(
        ".featured-slideshow__video"
      );
      if (videoActive) {
        videoActive.play().catch((error) => {
          console.error("Error playing video:", error);
        });
      }
    }

    if (prevSlide && prevSlide !== activeSlide) {
      const videoPrev = prevSlide.querySelector(".featured-slideshow__video");
      if (videoPrev) videoPrev.pause();
    }
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (activeSlide) {
      const videoActive = activeSlide.querySelector(
        ".featured-slideshow__video"
      );
      if (videoActive) videoActive.pause();
    }
  };

  const getBaseParams = () => ({
    speed: 1000,
    allowTouchMove: true,
    watchSlidesProgress: true,
  });

  // Maps the `animation_type` setting to Swiper effect params for a given swiper role.
  // For `creative` the media swiper uses `creative` (slides stay stacked without moving) so a
  // JS-driven clip-path wipe can run on top, while the content swiper cross-fades.
  const getEffectParams = (box, role) => {
    const anim = box.dataset.animation || "slide";
    if (anim === "fade") {
      return { effect: "fade", fadeEffect: { crossFade: true } };
    }
    if (anim === "creative") {
      if (role === "media") {
        return {
          effect: "creative",
          speed: 500,
          creativeEffect: {
            // Identity transforms keep the slides stacked and still — the JS-driven clip-path
            // wipe is the only visible motion. The 0.99 opacity is an imperceptible real
            // transition: `effect: 'creative'` runs with virtualTranslate, so Swiper detects the
            // transition end from a slide `transitionend`. With an all-identity effect nothing
            // transitions, that event never fires, and the swiper locks up after the first slide.
            prev: {
              translate: [0, 0, 0],
              rotate: [0, 0, 0],
              scale: 1,
              opacity: 0.99,
            },
            next: {
              translate: [0, 0, 0],
              rotate: [0, 0, 0],
              scale: 1,
              opacity: 0.99,
            },
          },
        };
      }
      return { effect: "fade", fadeEffect: { crossFade: true }, speed: 500 };
    }
    return { effect: "slide" };
  };

  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Looping behaviour by effect:
  // - `slide`: real `loop` (clones slides for seamless horizontal motion).
  // - `fade` / `creative`: `rewind` instead. These effects don't move horizontally, so wrapping
  //   back to the first slide looks identical to a seamless loop — but without slide clones, which
  //   with only 2-3 slides flash the section background mid-transition and break the loop.
  const applyLoopParams = (params, box) => {
    if (box.dataset.loop !== "true") return;
    if ((box.dataset.animation || "slide") === "slide") {
      params.loop = true;
      params.loopPreventsSliding = false;
    } else {
      params.rewind = true;
    }
  };

  const initMediaSwiper = (box) => {
    const el = box.querySelector(".featured-slideshow__media-swiper");
    if (!el) return null;
    if (el.querySelectorAll(".swiper-slide").length < 2) return null;

    const params = getBaseParams();
    Object.assign(params, getEffectParams(box, "media"));

    applyLoopParams(params, box);

    if (box.dataset.autoplay === "true" && !prefersReducedMotion()) {
      const delay = Number(box.dataset.autoplayDuration || 5) * 1000;
      params.autoplay = {
        delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
    }

    params.on = {
      slideChange: function () {
        playVideo(this);
      },
    };

    return new Swiper(el, params);
  };

  const initContentSwiper = (box) => {
    const el = box.querySelector(".featured-slideshow__content-swiper");
    if (!el) return null;
    if (el.querySelectorAll(".swiper-slide").length < 2) return null;

    const params = getBaseParams();
    Object.assign(params, getEffectParams(box, "content"));
    params.allowTouchMove = false;

    applyLoopParams(params, box);

    return new Swiper(el, params);
  };

  const linkSwipers = (mediaSwiper, contentSwiper) => {
    if (!mediaSwiper || !contentSwiper) return;
    mediaSwiper.controller.control = contentSwiper;
    contentSwiper.controller.control = mediaSwiper;
  };

  // Manual navigation (wheel / arrows / pagination) doesn't restart the autoplay countdown, so the
  // slide we land on inherits the previous slide's leftover time and can auto-advance immediately.
  // Restart it to give the new slide a full delay. Guarded by `running` so we don't resurrect
  // autoplay that the lifecycle paused (off-screen / keyboard focus).
  const resetAutoplay = (mediaSwiper) => {
    const autoplay = mediaSwiper && mediaSwiper.autoplay;
    if (!autoplay || !autoplay.running) return;
    autoplay.stop();
    autoplay.start();
  };

  const initPagination = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const paginationEl = box.querySelector(".featured-slideshow__pagination");
    if (!paginationEl) return;

    const bullets = Array.from(
      paginationEl.querySelectorAll(".featured-slideshow__bullet")
    );
    if (bullets.length === 0) return;

    const getActiveRealIndex = () =>
      mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    const hasAutoplay = box.dataset.autoplay === "true";

    const setActiveBullet = (realIndex) => {
      bullets.forEach((bullet, idx) => {
        const isActive = idx === realIndex;
        bullet.classList.toggle("is-active", isActive);
        bullet.setAttribute("aria-selected", isActive ? "true" : "false");
        // Under autoplay the bar is a pure CSS animation ((re)started by `is-active` landing on
        // the bullet) — no inline vars. The `--bullet-progress` var dance is no-autoplay only.
        if (hasAutoplay) return;
        if (isActive) {
          bullet.style.removeProperty("--bullet-progress");
        } else {
          bullet.style.setProperty("--bullet-progress", "0");
        }
      });
    };

    const activateBullet = (bullet) => {
      const idx = Number(bullet.dataset.slideIndex);
      if (Number.isNaN(idx)) return;
      if (idx === getActiveRealIndex()) return;
      if (mediaSwiper.params.loop) {
        mediaSwiper.slideToLoop(idx);
      } else {
        mediaSwiper.slideTo(idx);
      }
      resetAutoplay(mediaSwiper);
    };

    bullets.forEach((bullet) => {
      bullet.addEventListener("pointerenter", (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        activateBullet(bullet);
      });

      bullet.addEventListener("click", () => {
        activateBullet(bullet);
      });
    });

    // Track navigation direction so the progress bar fills/empties with it (CSS `.is-nav-prev`).
    // Compare real indices, with the loop/rewind wrap handled explicitly (last→first is forward,
    // first→last is back).
    let lastRealIndex = getActiveRealIndex();
    mediaSwiper.on("slideChange", () => {
      const realIndex = getActiveRealIndex();
      const total = bullets.length;
      let isPrev;
      if (lastRealIndex === total - 1 && realIndex === 0) {
        isPrev = false;
      } else if (lastRealIndex === 0 && realIndex === total - 1) {
        isPrev = true;
      } else {
        isPrev = realIndex < lastRealIndex;
      }
      lastRealIndex = realIndex;
      box.classList.toggle("is-nav-prev", isPrev);
      setActiveBullet(realIndex);
    });

    setActiveBullet(getActiveRealIndex());
  };

  // Mobile pagination peek-strip: below $lg the pagination scrolls horizontally with scroll-snap.
  // Settling the strip on another tab drives the whole slideshow; a slide change scrolls the strip
  // back to the active tab. The active-vs-nearest check keeps the two in sync without a feedback loop.
  const initPaginationPeek = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const strip = box.querySelector(".featured-slideshow__pagination");
    if (!strip) return;
    const bullets = Array.from(
      strip.querySelectorAll(".featured-slideshow__bullet")
    );
    if (bullets.length < 2) return;

    const mq = window.matchMedia("(max-width: 989px)");
    const getActiveIndex = () =>
      mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;
    const isRtl = getComputedStyle(strip).direction === "rtl";

    let scrollTimer = null;

    // Aligning the last tab to the strip's start needs more scroll than the strip has. Clamp the
    // request to the real range instead of handing the browser an out-of-range offset — iOS Safari
    // animates toward it and then snaps back, which reads as the tab jumping. (RTL scrollLeft
    // runs -max…0.)
    const clampDelta = (delta) => {
      const max = strip.scrollWidth - strip.clientWidth;
      const current = strip.scrollLeft;
      const next = Math.min(
        Math.max(current + delta, isRtl ? -max : 0),
        isRtl ? 0 : max
      );
      return next - current;
    };

    const scrollToActive = (smooth) => {
      const target = bullets[getActiveIndex()];
      if (!target) return;
      const delta = clampDelta(
        target.getBoundingClientRect().left - strip.getBoundingClientRect().left
      );
      if (delta === 0) return;
      strip.scrollBy({ left: delta, behavior: smooth ? "smooth" : "auto" });
    };

    // When the user settles the strip on a different tab, drive the whole slideshow to it.
    const onScrollSettled = () => {
      if (!mq.matches) return;
      const maxScroll = strip.scrollWidth - strip.clientWidth;
      let nearest;
      if (maxScroll > 0 && strip.scrollLeft >= maxScroll - 2) {
        nearest = bullets.length - 1;
      } else if (strip.scrollLeft <= 2) {
        nearest = 0;
      } else {
        const base = strip.getBoundingClientRect().left;
        let min = Infinity;
        nearest = 0;
        bullets.forEach((bullet, i) => {
          const distance = Math.abs(bullet.getBoundingClientRect().left - base);
          if (distance < min) {
            min = distance;
            nearest = i;
          }
        });
      }
      if (nearest === getActiveIndex()) return;
      if (mediaSwiper.params.loop) {
        mediaSwiper.slideToLoop(nearest);
      } else {
        mediaSwiper.slideTo(nearest);
      }
      resetAutoplay(mediaSwiper);
    };

    strip.addEventListener(
      "scroll",
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(onScrollSettled, 120);
      },
      { passive: true }
    );

    // A slide change (autoplay, media swipe, tab tap) keeps the strip aligned to the active tab.
    mediaSwiper.on("slideChange", () => {
      if (mq.matches) scrollToActive(true);
    });

    // Align the strip to the active tab on load and when entering the mobile breakpoint.
    if (mq.matches) scrollToActive(false);
    mq.addEventListener("change", (event) => {
      if (event.matches) scrollToActive(false);
    });
  };

  // Drives autoplay from viewport visibility + keyboard focus. Section-wide hover-pause
  // intentionally omitted: the section is typically fullscreen, so a hover-pause would mean
  // autoplay never runs.
  // Using autoplay.stop()/start() instead of pause()/resume() because pause() is cleared by the
  // next slideTo (e.g. pagination click), making a hovered pause "revive" after manual navigation.
  const initLifecycle = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    const hasAutoplay = box.dataset.autoplay === "true";

    let isVisible = false;
    let isFocusWithin = false;

    const sync = () => {
      if (isVisible) playVideo(mediaSwiper);
      else stopVideo(mediaSwiper);

      if (!hasAutoplay || !mediaSwiper.autoplay) return;
      const shouldRun = isVisible && !isFocusWithin && pageLoaded;
      if (shouldRun) {
        mediaSwiper.autoplay.start();
      } else {
        mediaSwiper.autoplay.stop();
      }
      // The CSS progress-bar countdown runs only while autoplay actually runs.
      box.classList.toggle("is-autoplay-running", mediaSwiper.autoplay.running);
    };

    // Swiper starts autoplay during init; stop it in the same task — the observer below and the
    // window `load` callback re-enable it once the section is visible and the page has loaded.
    sync();
    onPageLoad.push(sync);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        isVisible = entry.isIntersecting;
        sync();
      });
    });
    observer.observe(box);

    // A resize resets Swiper's autoplay timer while the running CSS progress animation keeps
    // its old schedule, so the bar would finish ahead of the actual slide change. Once the
    // resize settles, restart both together from zero.
    let resizeTimer = null;
    mediaSwiper.on("resize", () => {
      if (!hasAutoplay || !mediaSwiper.autoplay) return;
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (!mediaSwiper.autoplay.running) return;
        mediaSwiper.autoplay.stop();
        mediaSwiper.autoplay.start();
        box.classList.remove("is-autoplay-running");
        void box.offsetWidth;
        box.classList.add("is-autoplay-running");
      }, 150);
    });

    // `is-counting` mirrors Swiper's actual countdown: cleared while a slide transition runs
    // (with `waitForTransition` Swiper starts the next delay only after it ends), re-added on
    // the first tick of the fresh delay. Keyed to it, the CSS fill stays in sync with the real
    // slide change for autoplay and manual navigation alike; while paused (focus / off-screen)
    // the class stays and the bar falls back to full. The tick handler only toggles a class —
    // no per-frame style writes — and autoplay is off during page load, so none of this runs
    // in the load window.
    if (hasAutoplay) {
      mediaSwiper.on("slideChange", () => {
        box.classList.remove("is-counting");
      });
      mediaSwiper.on("autoplayTimeLeft", (_s, _time, progress) => {
        // During the transition Swiper keeps ticking with `paused: true` and timeLeft frozen
        // at the FULL delay (progress = 1) — only the paused flag separates that from the
        // real countdown, which starts ticking at the transition's end.
        if (mediaSwiper.autoplay.paused) return;
        if (progress > 0.9 && !box.classList.contains("is-counting")) {
          box.classList.add("is-counting");
        }
      });
    }

    // Only keyboard focus pauses autoplay. Mouse-clicking a pagination tab also gives it focus,
    // but `:focus-visible` will be false in that case, so we skip it.
    box.addEventListener("focusin", (event) => {
      const target = event.target;
      if (!target || typeof target.matches !== "function") return;
      if (!target.matches(":focus-visible")) return;
      isFocusWithin = true;
      sync();
    });
    box.addEventListener("focusout", (event) => {
      if (!isFocusWithin) return;
      if (box.contains(event.relatedTarget)) return;
      isFocusWithin = false;
      sync();
    });
  };

  const initPaginationHoverPause = (box, mediaSwiper) => {
    if (!mediaSwiper || box.dataset.autoplay !== "true") return;
    if (!mediaSwiper.params.autoplay?.enabled) return;

    const paginationEl = box.querySelector(".featured-slideshow__pagination");
    if (!paginationEl) return;

    let isHovered = false;

    const pauseAutoplay = () => {
      const autoplay = mediaSwiper.autoplay;
      if (!autoplay || !autoplay.running) return;
      autoplay.pause();
      box.classList.add("is-autoplay-hover-paused");
    };

    paginationEl.addEventListener("pointerenter", (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      if (isHovered) return;
      isHovered = true;
      pauseAutoplay();
    });

    paginationEl.addEventListener("pointerleave", (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      if (!isHovered) return;
      isHovered = false;
      box.classList.remove("is-autoplay-hover-paused");
      const autoplay = mediaSwiper.autoplay;
      if (autoplay && autoplay.running && autoplay.paused) autoplay.resume();
    });

    const reapply = () => {
      if (isHovered) pauseAutoplay();
    };
    mediaSwiper.on("autoplayStart", reapply);
    mediaSwiper.on("autoplayResume", reapply);
  };

  // Keeps the hover-zone's bottom edge above the info bar by exposing the info bar height as a
  // CSS var on the section root. The hover-zone uses `bottom: var(--featured-slideshow-info-height)`.
  const initInfoHeightVar = (box) => {
    const info = box.querySelector(".featured-slideshow__info");
    if (!info) {
      box.style.setProperty("--featured-slideshow-info-height", "0px");
      return;
    }
    const update = () => {
      box.style.setProperty(
        "--featured-slideshow-info-height",
        `${info.offsetHeight}px`
      );
    };
    update();
    if (typeof ResizeObserver === "function") {
      const ro = new ResizeObserver(update);
      ro.observe(info);
    } else {
      window.addEventListener("resize", update);
    }
  };

  const initHoverCard = (box, mediaSwiper) => {
    // No early return on a missing swiper: with a single slide the media swiper isn't initialized,
    // but the hover card should still show (active index is just always 0, no slideChange to track).
    if (box.dataset.hoverCard !== "true") return;

    const zone = box.querySelector(".featured-slideshow__hover-zone");
    if (!zone) return;

    // `cards` are the visual elements that follow the cursor (measured + transformed). `links` are
    // their full-zone clickable parents — only the active one stays interactive (gated in CSS off
    // `is-active`), so a click anywhere in the zone navigates to the current slide's link.
    const cards = Array.from(
      zone.querySelectorAll(".featured-slideshow__hover-card-wrapper")
    );
    if (cards.length === 0) return;
    const links = Array.from(
      zone.querySelectorAll(".featured-slideshow__hover-card-link")
    );

    const getActiveIndex = () =>
      mediaSwiper ? mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0 : 0;

    // Position the card with trailing inertia: `target` is where the cursor wants it, `current`
    // is the eased position actually rendered. A rAF loop runs only while the cursor is in the
    // zone. Because the loop always positions whichever card is active, a slideChange swap can't
    // leave the new card stranded at translate(0,0).
    const EASE = 0.14;
    const reduced = prefersReducedMotion();

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let rafId = null;

    const setActiveCard = (index) => {
      cards.forEach((card, i) => {
        card.classList.toggle("is-active", i === index);
      });
      links.forEach((link, i) => {
        link.classList.toggle("is-active", i === index);
      });
    };

    // Resting-position inset for the card. The zone still catches the cursor right up to its edge,
    // so hover/darken stay full (no dead strip) — only the card rests this far in. `TOP_GAP` always
    // (clears the header / section top); the side gap only without arrows, where the zone reaches
    // the screen edges (with arrows the zone is already inset to the central columns).
    const TOP_GAP = 24;
    const SIDE_GAP = zone.classList.contains(
      "featured-slideshow__hover-zone--has-arrows"
    )
      ? 0
      : 24;

    const updateTarget = (clientX, clientY) => {
      const activeCard = cards[getActiveIndex()];
      if (!activeCard) return;
      const zoneRect = zone.getBoundingClientRect();
      const cardWidth = activeCard.offsetWidth;
      const cardHeight = activeCard.offsetHeight;

      const x = clientX - zoneRect.left - cardWidth / 2;
      const y = clientY - zoneRect.top - cardHeight / 2;

      targetX = Math.max(
        SIDE_GAP,
        Math.min(x, zoneRect.width - cardWidth - SIDE_GAP)
      );
      targetY = Math.max(TOP_GAP, Math.min(y, zoneRect.height - cardHeight));
    };

    const applyTransform = () => {
      const activeCard = cards[getActiveIndex()];
      if (activeCard) {
        activeCard.style.transform = `translate(${currentX}px, ${currentY}px)`;
      }
    };

    const tick = () => {
      currentX += (targetX - currentX) * EASE;
      currentY += (targetY - currentY) * EASE;
      applyTransform();
      rafId = requestAnimationFrame(tick);
    };

    const stopLoop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    setActiveCard(getActiveIndex());
    if (mediaSwiper) {
      mediaSwiper.on("slideChange", () => {
        setActiveCard(getActiveIndex());
        // The freshly-activated card picks up the current eased position immediately.
        applyTransform();
      });
    }

    const handleEnter = (event) => {
      updateTarget(event.clientX, event.clientY);
      // Snap to the cursor on appearance — the trailing weight only applies to movement.
      currentX = targetX;
      currentY = targetY;
      applyTransform();
      box.classList.add("hover-card-visible");
      if (!reduced) {
        stopLoop();
        rafId = requestAnimationFrame(tick);
      }
    };

    zone.addEventListener("mouseenter", handleEnter);
    zone.addEventListener("mousemove", (event) => {
      // Missed-enter fallback: if the cursor was already parked inside the zone when the
      // listeners attached (deferred init during page load), there's no boundary crossing and
      // `mouseenter` never fires — the first move in the not-shown state acts as the enter.
      if (!box.classList.contains("hover-card-visible")) {
        handleEnter(event);
        return;
      }
      updateTarget(event.clientX, event.clientY);
      if (reduced) {
        currentX = targetX;
        currentY = targetY;
        applyTransform();
      }
    });
    zone.addEventListener("mouseleave", () => {
      box.classList.remove("hover-card-visible");
      stopLoop();
    });
  };

  // The hover cards ship inert inside <template> (an SVG-heavy, pointer-only affordance). They
  // join the DOM — and initHoverCard runs — only on hover-capable devices; the insert itself is
  // cheap, so it happens right at init and the card is ready for the first hover. Touch devices
  // (and the Lighthouse mobile pass, which emulates one) never pay this DOM/style cost at all.
  const initHoverCardLazy = (box, mediaSwiper) => {
    if (box.dataset.hoverCard !== "true") return;
    const zone = box.querySelector(".featured-slideshow__hover-zone");
    if (!zone) return;

    const template = zone.querySelector("template");
    if (!template) {
      initHoverCard(box, mediaSwiper);
      return;
    }

    let materialized = false;
    const materialize = () => {
      if (materialized) return;
      materialized = true;
      zone.appendChild(template.content);
      template.remove();
      initHoverCard(box, mediaSwiper);
    };
    const hoverMq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (hoverMq.matches) {
      materialize();
    } else {
      // A pointer can appear later (e.g. a mouse plugged into a tablet).
      hoverMq.addEventListener("change", (event) => {
        if (event.matches) materialize();
      });
    }
  };

  // One zone spanning the slide (it flips by the pointer side), or — with the hover card owning the
  // middle — two one-way zones flanking it.
  const initCursorArrows = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    if (box.dataset.cursorArrows !== "true") return;
    if (typeof window.initCursorArrowZone !== "function") return;

    const zones = box.querySelectorAll(".featured-slideshow__arrow-zone");
    if (zones.length === 0) return;

    // Loop / rewind always wrap, so only a finite slider can reach an inert end: there the click is
    // a no-op and CSS marks the arrow not-allowed (see `.is-disabled`).
    const finite = !mediaSwiper.params.loop && !mediaSwiper.params.rewind;

    zones.forEach((zone) => {
      const nav = window.initCursorArrowZone(zone, {
        onNavigate: (direction) => {
          if (direction === "next") {
            mediaSwiper.slideNext();
          } else {
            mediaSwiper.slidePrev();
          }
          resetAutoplay(mediaSwiper);
        },
        isEndReached: (direction) => {
          if (!finite) return false;
          return direction === "prev"
            ? mediaSwiper.isBeginning
            : mediaSwiper.isEnd;
        },
      });

      if (finite) mediaSwiper.on("slideChange", nav.sync);
    });
  };

  // Creative animation: the incoming media slide is wiped in bottom-to-top via clip-path, while
  // the slide we came from stays fully visible underneath as the underlay; all others are clipped
  // away. `effect: 'creative'` keeps the slides stacked (and the active one on top) without moving
  // them. The clip-path lives on `__media`, not the slide itself — Swiper drives `transition` on
  // the slide, so keeping our transition on a child element avoids a conflict.
  const initCreativeMedia = (box, mediaSwiper) => {
    if (!mediaSwiper) return;
    if (box.dataset.animation !== "creative") return;

    const HIDDEN = "inset(100% 0 0 0)";
    const SHOWN = "inset(0 0 0 0)";

    const getMedia = (slide) =>
      slide ? slide.querySelector(".featured-slideshow__media") : null;

    const setInstant = (media, value) => {
      if (!media) return;
      media.style.transition = "none";
      media.style.clipPath = value;
    };

    if (prefersReducedMotion()) {
      // No wipe: Swiper's creative z-index keeps the active slide on top for an instant cut.
      mediaSwiper.slides.forEach((slide) => setInstant(getMedia(slide), SHOWN));
      return;
    }

    mediaSwiper.slides.forEach((slide, i) => {
      setInstant(
        getMedia(slide),
        i === mediaSwiper.activeIndex ? SHOWN : HIDDEN
      );
    });

    // Only wipe on a real slide change. `slideChange` also fires when Swiper re-shuffles its
    // looped slides after a resize (e.g. a dynamic announcement bar changing the section height
    // on scroll) — there the logical slide is unchanged, so replaying the wipe would be a glitch.
    let lastRealIndex = mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;

    mediaSwiper.on("slideChange", () => {
      const realIndex = mediaSwiper.realIndex ?? mediaSwiper.activeIndex ?? 0;
      if (realIndex === lastRealIndex) return;
      lastRealIndex = realIndex;

      const { slides, activeIndex, previousIndex } = mediaSwiper;
      slides.forEach((slide, i) => {
        const media = getMedia(slide);
        if (!media) return;
        if (i === activeIndex) {
          // Force a reflow so the wipe always starts from the fully-clipped state.
          media.style.transition = "none";
          media.style.clipPath = HIDDEN;
          void media.offsetWidth;
          media.style.transition =
            "clip-path 0.7s cubic-bezier(0.45, 0.75, 0.1, 0.9)";
          media.style.clipPath = SHOWN;
        } else if (i === previousIndex) {
          setInstant(media, SHOWN);
        } else {
          setInstant(media, HIDDEN);
        }
      });
    });
  };

  // Darkens the slide while the cursor is over the hover-zone (card visible) or the arrow-zones.
  // Runs only when the hover card is enabled — the dim exists to keep the card legible, and it
  // persists across the arrow-zones since the card is still relevant there. Moving onto the
  // content / pagination (or leaving the section) lifts it.
  const initDarken = (box) => {
    if (box.dataset.hoverCard !== "true") return;

    const zones = [];
    const hoverZone = box.querySelector(".featured-slideshow__hover-zone");
    if (hoverZone) zones.push(hoverZone);
    box
      .querySelectorAll(".featured-slideshow__arrow-zone")
      .forEach((zone) => zones.push(zone));
    if (zones.length === 0) return;

    const hovered = new Set();
    zones.forEach((zone) => {
      zone.addEventListener("mouseenter", () => {
        hovered.add(zone);
        box.classList.add("is-darkened");
      });
      // Missed-enter fallback: cursor parked inside the zone during deferred init (see the
      // hover-card mousemove note).
      zone.addEventListener("mousemove", () => {
        if (hovered.has(zone)) return;
        hovered.add(zone);
        box.classList.add("is-darkened");
      });
      zone.addEventListener("mouseleave", () => {
        hovered.delete(zone);
        if (hovered.size === 0) box.classList.remove("is-darkened");
      });
    });
  };

  // Two-finger horizontal trackpad swipe → prev/next. Only horizontal-dominant `wheel` gestures are
  // captured (vertical wheel keeps scrolling the page); for those we `preventDefault` so the browser
  // back/forward swipe doesn't fire over the slideshow. A trackpad emits a long momentum stream after
  // the flick, so a navigation starts a fixed cooldown (NOT reset per event — that would keep it
  // locked for the whole inertia): one slide per swipe, and by the time the cooldown ends the inertia
  // tail is below the `8` threshold and won't re-trigger. Listener is on the section root, not the
  // media swiper, since the info/hover layers sit above it and would otherwise swallow the event.
  const COOLDOWN = 700;
  const initWheelNav = (box, mediaSwiper) => {
    if (!mediaSwiper) return;

    let locked = false;

    box.addEventListener(
      "wheel",
      (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();

        if (locked || Math.abs(event.deltaX) < 8) return;
        locked = true;
        window.setTimeout(() => {
          locked = false;
        }, COOLDOWN);

        if (event.deltaX > 0) {
          mediaSwiper.slideNext();
        } else {
          mediaSwiper.slidePrev();
        }
        resetAutoplay(mediaSwiper);
      },
      { passive: false }
    );
  };

  const initSection = (section) => {
    if (!section || !section.classList.contains("featured-slideshow-section"))
      return;

    const box = section.querySelector(".featured-slideshow");
    if (!box) return;

    const mediaSwiper = initMediaSwiper(box);
    const contentSwiper = initContentSwiper(box);

    linkSwipers(mediaSwiper, contentSwiper);
    initCreativeMedia(box, mediaSwiper);
    initPagination(box, mediaSwiper);
    initPaginationPeek(box, mediaSwiper);
    initInfoHeightVar(box);
    initHoverCardLazy(box, mediaSwiper);
    initCursorArrows(box, mediaSwiper);
    initDarken(box);
    initWheelNav(box, mediaSwiper);
    initLifecycle(box, mediaSwiper);
    initPaginationHoverPause(box, mediaSwiper);
  };

  initSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    initSection(event.target);
  });
})();
